import 'twin.macro'

import { yupResolver } from '@hookform/resolvers/yup'
import Button from 'components/UI/Buttons/Button'
import MaskedInput from 'components/UI/Inputs/MaskedInput'
import OtpInput from 'components/UI/Inputs/OtpInput'
import { useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSendOTPMutation, useVerifyOTPMutation } from 'redux/api/authApi'
import { ModeApp, recaptchaSitekey } from 'utils/constants'
import { PhoneMask } from 'utils/i-masks'
import * as yup from 'yup'

const lang = localStorage.getItem('i18nextLng')
const phoneSchema = yup
  .object({
    phone: yup
      .string()
      .min(
        11,
        `${lang == 'ru' ? 'Телефон должен быть не менее 11 символов' : 'Телефон кемінде 11 таңбадан тұруы керек'}`
      )
      .max(11)
  })
  .required(`${lang === 'ru' ? 'Заполните это поле, пожалуйста' : 'Осы өрісті толтырыңыз'}`)

const otpSchema = yup.object({
  code: yup
    .string()
    .min(6, `${lang == 'ru' ? 'Телефон должен быть не менее 6 символов' : 'Телефон кемінде 6 таңбадан тұруы керек'}`)
    .required(`${lang === 'ru' ? 'Заполните это поле, пожалуйста' : 'Осы өрісті толтырыңыз'}`)
})

type FormSchemaValues = {
  phone: string
  code: string
}

type AuthContentProps = {
  onCloseModal: () => void
}

const isProduction = ModeApp === 'production'

const AuthContent = ({ onCloseModal }: AuthContentProps) => {
  const [isPhoneSend, setIsPhoneSend] = useState(false)
  const [reCaptchaToken, setReCaptchaToken] = useState<string | null>(null)

  const [sendOtp] = useSendOTPMutation()
  const [verifyOtp, { isLoading: isVerifyOtpLoading }] = useVerifyOTPMutation()

  const {
    t,
    i18n: { language }
  } = useTranslation()

  // Forms
  const {
    control,
    formState: { isValid },
    getValues,
    setError
  } = useForm<FormSchemaValues>({
    defaultValues: !isPhoneSend ? { phone: '' } : { code: '' },
    resolver: yupResolver(!isPhoneSend ? phoneSchema : otpSchema),
    mode: 'onChange'
  })

  const isPhoneBtnValid = isProduction ? !reCaptchaToken || !isValid : !isValid

  const handleReCaptchaChange = (token: string | null) => {
    setReCaptchaToken(token)
  }

  const handlePhoneConfirm = async () => {
    try {
      const { phone } = getValues()
      await sendOtp(`+${phone}`).unwrap()
      setIsPhoneSend(true)
    } catch (error) {
      const typedErr = error as {
        data: {
          phone: Array<string>
        }
        status: number
      }
      typedErr.data.phone && setError('phone', { type: 'custom', message: typedErr.data.phone[0] })
    }
  }

  const handleOtpConfirm = async () => {
    try {
      if (isProduction && !reCaptchaToken) return
      const { phone, code } = getValues()
      const dataForOtp = {
        phone: `+${phone}`,
        code: code,
        recaptcha: isProduction ? reCaptchaToken : undefined
      }
      await verifyOtp(dataForOtp).unwrap()
      onCloseModal()
    } catch (error) {
      const typedErr = error as {
        data: {
          detail: string
        }
        status: number
      }
      typedErr.data.detail && setError('code', { type: 'custom', message: typedErr.data.detail })
    }
  }

  return (
    <div tw='mt-5'>
      {!isPhoneSend ? (
        <MaskedInput
          label={t('flow-mobile-number')}
          placeholder='+7 (---) --- -- --'
          name='phone'
          control={control}
          maskOptions={{
            mask: PhoneMask
          }}
        />
      ) : (
        <OtpInput
          shouldAutoFocus
          numInputs={6}
          label={t('flow-enter-code') as string}
          control={control}
          name={'code'}
        />
      )}
      <div tw='mt-[15px]'>
        {!isPhoneSend ? (
          <div tw='space-y-3'>
            {isProduction && <ReCAPTCHA hl={language} sitekey={recaptchaSitekey} onChange={handleReCaptchaChange} />}
            <Button variant='secondary' disabled={isPhoneBtnValid} onClick={handlePhoneConfirm}>
              {t('flow-get-sms')}
            </Button>
          </div>
        ) : (
          <Button variant='secondary' onClick={handleOtpConfirm} disabled={!isValid} loading={isVerifyOtpLoading}>
            {t('flow-aprove')}
          </Button>
        )}
      </div>
    </div>
  )
}
export default AuthContent
